// ============================================================================
// CHANGO V5.0 — Company Name Change Modal
// ============================================================================
// Task:     §9.6.2e Company name change request modal
// Purpose:  Collect new company name + license + SMS code + submit request
// Reference: §9.6.2e flow
// ============================================================================

import React, { useState, useCallback } from 'react';
import { api, HttpError } from '../api/client.js';
import SmsCodeInput from './SmsCodeInput.jsx';

export default function CompanyNameChangeModal({ isOpen, onClose, currentName, phone, onSuccess }) {
  const [newName, setNewName] = useState('');
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseUrl, setLicenseUrl] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [licenseUploading, setLicenseUploading] = useState(false);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate: PNG / JPG only, ≤ 5MB
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('仅支持 PNG/JPG 格式');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('文件大小不能超过 5MB');
      return;
    }

    setError(null);
    setLicenseUploading(true);
    try {
      // Upload to OSS via /api/upload/license
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.upload('/api/upload/license', formData);
      const nextLicenseUrl = res.url || res.file_url || res.license_file_url;
      if (!nextLicenseUrl) throw new Error('上传接口未返回执照 URL');

      setLicenseFile(file);
      setLicenseUrl(nextLicenseUrl);
      setError(null);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `上传失败: ${err.message}`
          : '上传失败，请重试'
      );
      setLicenseFile(null);
      setLicenseUrl('');
    } finally {
      setLicenseUploading(false);
    }
  }, []);

  const handleSendSmsCode = useCallback(async ({ phone: nextPhone, scope }) => {
    return api.post('/api/sms/send-code', { phone: nextPhone, scope });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    // Validation
    if (!newName.trim()) {
      setError('请输入新的公司全称');
      return;
    }
    if (!licenseUrl) {
      setError('请上传新的营业执照照片');
      return;
    }
    if (!smsCode || smsCode.length !== 6) {
      setError('请输入有效的短信验证码');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const payload = {
        new_company_name: newName.trim(),
        new_license_url: licenseUrl,
        sms_code: smsCode,
        phone
      };

      const res = await api.post('/api/workspace/company-name/change-request', payload);

      if (res.success) {
        setError(null);
        onSuccess?.();
        // Reset form
        setNewName('');
        setLicenseFile(null);
        setLicenseUrl('');
        setSmsCode('');
        onClose?.();
      } else {
        setError(res.message || '提交失败');
      }
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.code === 'COMPANY_CHANGE_RATE_LIMIT') {
          const nextEligible = err.details?.next_eligible_at;
          if (nextEligible) {
            const d = new Date(nextEligible);
            setError(`您 30 天内已申请过变更，请于 ${d.toLocaleDateString()} 后再试`);
          } else {
            setError('30 天内已申请过变更，请稍后再试');
          }
        } else if (err.code === 'COMPANY_CHANGE_PENDING') {
          setError('您已有待审核的变更申请，请等待管理员处理');
        } else {
          setError(`错误: ${err.message}`);
        }
      } else {
        setError(err.message || '提交失败，请重试');
      }
    } finally {
      setLoading(false);
    }
  }, [newName, licenseUrl, smsCode, phone, onSuccess, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
        {/* Close button */}
        <button
          type="button"
          className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"
          onClick={() => onClose?.()}
          disabled={loading}
        >
          ✕
        </button>

        <h2 className="mb-4 text-lg font-semibold text-slate-800">申请变更公司全称</h2>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Name (read-only) */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              <span className="text-red-600">*</span> 当前公司全称
            </label>
            <input
              type="text"
              value={currentName}
              readOnly
              className="mt-1 block w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600"
            />
          </div>

          {/* New Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              <span className="text-red-600">*</span> 新公司全称
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="请输入新的公司全称"
              className="mt-1 block w-full rounded border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"
              disabled={loading}
            />
          </div>

          {/* License Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              <span className="text-red-600">*</span> 新营业执照照片
            </label>
            <p className="mt-1 text-xs text-slate-500">PNG / JPG，单文件 ≤ 5MB</p>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileChange}
              disabled={loading || licenseUploading}
              className="mt-2 block w-full text-sm file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm"
            />
            {licenseFile && (
              <p className="mt-2 text-xs text-emerald-700">
                ✓ {licenseFile.name} ({(licenseFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          {/* SMS Code Input */}
          <div>
            <SmsCodeInput
              scope="change_company_name"
              phone={phone}
              codeValue={smsCode}
              onCodeChange={setSmsCode}
              onSendCode={handleSendSmsCode}
              disabled={loading}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-4">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={() => onClose?.()}
              disabled={loading}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={!newName.trim() || !licenseUrl || !smsCode || loading || licenseUploading}
            >
              {loading ? '提交中...' : '提交申请'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
